import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api';

const AuthContext = createContext(null);

/**
 * Holds the signed-in user for the whole app. The session itself lives in an
 * httpOnly cookie, so nothing sensitive is kept here — this is only what the
 * interface needs to render, plus the unread badge.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [unread, setUnread] = useState(0);
  // 'loading' until the first /me answers, so pages do not flash a sign-in
  // prompt at somebody who is already signed in.
  const [status, setStatus] = useState('loading');

  const refresh = useCallback(async () => {
    try {
      const data = await api.fetchSession();
      setUser(data.user);
      setUnread(data.unread || 0);
      setStatus('ready');
      return data.user;
    } catch {
      setUser(null);
      setUnread(0);
      setStatus('ready');
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signIn = useCallback(async (credential) => {
    const data = await api.signInWithGoogle(credential);
    setUser(data.user);
    setStatus('ready');
    await refresh();
    return data.user;
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      setUser(null);
      setUnread(0);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    unread,
    setUnread,
    status,
    isLoading: status === 'loading',
    isAdmin: Boolean(user && user.isAdmin),
    signIn,
    signOut,
    refresh,
  }), [user, unread, status, signIn, signOut, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;
